import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, ConfirmDialog, DataGrid, Modal, StatusBadge, useToast,
  type DataGridColumn,
} from '@kss/ui';
import { Plus, RefreshCw, Trash2, Pencil } from 'lucide-react';
import { encodeId } from '../../utils/idCodec';
import { StandardAddDropdown } from '../shared/AddDropdown';
import type { ApiUser } from '@kss/api';
import { userApiService } from '@kss/api';

export default function Users() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [rows, setRows] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [detailRow, setDetailRow] = useState<ApiUser | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ApiUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await userApiService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load users';
      setError(msg); toastRef.current.error('Load failed', msg);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.userId) return;
    setDeleting(true);
    try {
      await userApiService.delete(confirmDelete.userId);
      toast.success('Deleted', `${confirmDelete.name} removed`);
      setConfirmDelete(null); await load();
    } catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  const columns: DataGridColumn<ApiUser>[] = [
    { accessor: 'name', title: 'Name', sortable: true, filterable: true },
    { accessor: 'email', title: 'Email', sortable: true, filterable: true },
    { accessor: 'phone', title: 'Phone', sortable: true, render: (r) => r.phone || '—' },
    { accessor: 'role', title: 'Role', sortable: true, filterable: true, render: (r) => r.role || '—' },
    { accessor: 'isActive', title: 'Status', sortable: true,
      render: (r) => <StatusBadge status={r.isActive ? 'active' : 'inactive'} /> },
    { accessor: 'userId', title: 'Actions', width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
            onClick={(e) => { e.stopPropagation(); if (r.userId) navigate(`/panel/users/${encodeId(r.userId)}`); }} title="Edit">
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
            onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ) },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <DataGrid<ApiUser>
        title={<span className="hidden sm:inline">All users</span>}
        columnPicker onRowClick={(row) => setDetailRow(row)} data={rows} columns={columns}
        selectable selectedIds={selected} onSelectedChange={setSelected}
        defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <StandardAddDropdown basePath="/panel/users" newLabel="New user" service={userApiService} templateFields={['userName', 'email', 'phone', 'firstName', 'lastName', 'storeId', 'status']} module="User" />
          </div>
        }
      />
      <Modal open={!!detailRow} onClose={() => setDetailRow(null)} title={detailRow ? `User · ${detailRow.name}` : ''} width={520}
        footer={<>
          <Button variant="outline" onClick={() => setDetailRow(null)}>Close</Button>
          {detailRow?.userId && (
            <Button onClick={() => { const id = detailRow.userId!; setDetailRow(null); navigate(`/panel/users/${encodeId(id)}`); }}>Edit</Button>
          )}
        </>}>
        {detailRow && (
          <div className="grid gap-3 rounded-md border border-border bg-surface-card-hover p-4 text-sm">
            <DetailRow label="Name" value={detailRow.name} />
            <DetailRow label="Email" value={detailRow.email} />
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="Phone" value={detailRow.phone} />
              <DetailRow label="Role" value={detailRow.role} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="Status" value={<StatusBadge status={detailRow.isActive ? 'active' : 'inactive'} />} />
              <DetailRow label="Last login" value={detailRow.lastLoginAt ? new Date(detailRow.lastLoginAt).toLocaleString() : ''} />
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete} title="Delete user"
        message={confirmDelete ? `This will permanently remove "${confirmDelete.name}". This action cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value || <em className="text-content-muted">—</em>}</span>
    </div>
  );
}
